// /admin/compliance/clients/:id — per-client KYC review (portal-kyc Phase E).
//
// Data source (PLAN §6b): GET /api/admin/compliance/clients/:id
//   → { client, documents: DocumentDto[], consent:{latest_at,latest_version},
//       required: string[], rollup }
//
// Two-column layout (admin_ux client_detail_page):
//   Left  — client identity, rollup chip, finalise gate, consent summary,
//           required-category progress.
//   Right — per-document cards grouped by kyc_category with state-conditional
//           actions: Download always; Accept/Reject only from in_review.
//
// State changes (accept / reject / finalise) re-fetch the packet afterwards —
// the simplest, safest update path (matches the Phase D portal refetch model).
//
// RBAC mirror, NOT enforcement (REQ-004): the server is the source of truth
// (requireRbac on every endpoint). The UI hides/disables write affordances by
// role so a read-only role (lead / readonly) sees disabled buttons with a
// tooltip rather than a 403 surprise. Download stays enabled for every role
// that can read this page.
//
// Chips render inline (support-cx precedent #7 — StatusChip NOT extended). All
// copy here is operational language, not regulatory prose (REQ-008).

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  getKycStatusLabel,
  KYC_CATEGORIES,
  type DocumentDto,
  type KycReviewStatus,
  type KycStatus,
} from '@shared';
import { apiGet, apiPost, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';

// Roles allowed to accept / reject / finalise in the UI. Mirrors PLAN §11a
// write_accept_reject / write_finalise (super, admin, compliance). lead +
// readonly can READ but see disabled action buttons.
const COMPLIANCE_WRITE_ROLES = new Set(['super', 'admin', 'compliance']);

interface ClientDto {
  id: string;
  name: string;
  entity_type: string;
  country: string;
  sanctions_status?: 'pending' | 'clear' | 'flagged';
  kyc?: KycStatus;
  status?: string;
  registration_number?: string;
}

interface ClientPacket {
  client: ClientDto;
  documents: DocumentDto[];
  consent: { latest_at: string | null; latest_version: number | null };
  required: string[];
  rollup: KycStatus;
}

// ── chip helpers (inline, per support-cx precedent #7) ──────────────────────

function rollupChipTone(s: KycStatus): string {
  switch (s) {
    case 'current': return 'green';
    case 'in_review': return 'accent';
    case 'rejected': return 'red';
    case 'expired': return 'red';
    case 'refresh': return 'amber';
    case 'pending': default: return '';
  }
}

function docChipTone(s: KycReviewStatus | null): string {
  switch (s) {
    case 'accepted': return 'green';
    case 'in_review': return 'accent';
    case 'rejected': return 'amber';
    case 'uploaded': return '';
    case 'superseded': return '';
    default: return '';
  }
}

function docStateLabel(s: KycReviewStatus | null): string {
  switch (s) {
    case 'uploaded': return 'Draft (not submitted)';
    case 'in_review': return 'In review';
    case 'accepted': return 'Accepted';
    case 'rejected': return 'Rejected';
    case 'superseded': return 'Replaced';
    default: return 'Unknown';
  }
}

function categoryLabel(key: string | null): string {
  if (!key) return 'Uncategorised';
  return KYC_CATEGORIES.find((c) => c.key === key)?.label ?? key;
}

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// ── Reject-reason modal ──────────────────────────────────────────────────────

function RejectModal({ clientName, doc, onConfirm, onCancel, busy, error }: {
  clientName: string;
  doc: DocumentDto;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
  busy: boolean;
  error: string | null;
}) {
  const [reason, setReason] = useState('');
  const trimmed = reason.trim();
  // Server enforces 5–2000 chars; mirror it client-side so the operator gets
  // immediate feedback before the round-trip.
  const tooShort = trimmed.length < 5;
  const tooLong = trimmed.length > 2000;
  const canSubmit = !tooShort && !tooLong && !busy;

  return (
    <div className="kyc-modal-backdrop" role="dialog" aria-modal="true" aria-label="Reject document">
      <div className="kyc-modal card">
        <h3 style={{ fontSize: 17, margin: 0 }}>Reject document</h3>
        <p style={{ fontSize: 13, color: 'var(--fg-soft)', marginTop: 8, lineHeight: 1.5 }}>
          {clientName} · {categoryLabel(doc.kyc_category)} · <span style={{ fontFamily: 'var(--mono)' }}>{doc.filename}</span>
        </p>
        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginTop: 14, marginBottom: 6 }}>
          Reason for rejection <span style={{ color: 'var(--red)' }}>*</span>
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={5}
          maxLength={2000}
          placeholder="Explain what the client needs to correct or resubmit…"
          style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit', fontSize: 13.5, padding: 10 }}
          disabled={busy}
        />
        <div style={{ fontSize: 11.5, color: 'var(--fg-mute)', marginTop: 4 }}>
          {trimmed.length}/2000 · minimum 5 characters
        </div>

        {error && (
          <div style={{ color: 'var(--red)', fontSize: 13, marginTop: 12 }}>{error}</div>
        )}

        <div className="kyc-modal-actions">
          <button type="button" className="btn ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button
            type="button"
            className="btn accent"
            onClick={() => onConfirm(trimmed)}
            disabled={!canSubmit}
          >
            {busy ? 'Rejecting…' : 'Reject document'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Finalise confirm modal ───────────────────────────────────────────────────

function FinaliseModal({ clientName, onConfirm, onCancel, busy, error }: {
  clientName: string;
  onConfirm: () => void;
  onCancel: () => void;
  busy: boolean;
  error: string | null;
}) {
  return (
    <div className="kyc-modal-backdrop" role="dialog" aria-modal="true" aria-label="Finalise client KYC">
      <div className="kyc-modal card">
        <h3 style={{ fontSize: 17, margin: 0 }}>Finalise client KYC</h3>
        <p style={{ fontSize: 13.5, color: 'var(--fg-soft)', marginTop: 12, lineHeight: 1.55 }}>
          You are about to finalise KYC for <strong>{clientName}</strong>. This clears the client to
          submit briefs and records the outcome in the audit log.
        </p>
        {error && (
          <div style={{ color: 'var(--red)', fontSize: 13, marginTop: 12 }}>{error}</div>
        )}
        <div className="kyc-modal-actions">
          <button type="button" className="btn ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" className="btn accent" onClick={onConfirm} disabled={busy}>
            {busy ? 'Finalising…' : 'Finalise KYC'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function ComplianceClient() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const canWrite = COMPLIANCE_WRITE_ROLES.has(user?.role ?? '');

  const [packet, setPacket] = useState<ClientPacket | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Pending per-doc action (so we can disable just the affected card).
  const [busyDocId, setBusyDocId] = useState<string | null>(null);
  const [rejectFor, setRejectFor] = useState<DocumentDto | null>(null);
  const [rejectBusy, setRejectBusy] = useState(false);
  const [rejectError, setRejectError] = useState<string | null>(null);

  const [finaliseOpen, setFinaliseOpen] = useState(false);
  const [finaliseBusy, setFinaliseBusy] = useState(false);
  const [finaliseError, setFinaliseError] = useState<string | null>(null);

  const [showSuperseded, setShowSuperseded] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    const p = await apiGet<ClientPacket>(`/api/admin/compliance/clients/${id}`);
    setPacket(p);
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiGet<ClientPacket>(`/api/admin/compliance/clients/${id}`)
      .then((p) => { if (!cancelled) setPacket(p); })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.serverMessage : 'Could not load the client KYC packet.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  // ── Actions ────────────────────────────────────────────────────────────────

  async function downloadDoc(documentId: string) {
    setActionError(null);
    try {
      const r = await apiGet<{ download_url: string; expires_at: string }>(
        `/api/admin/compliance/documents/${documentId}/download`,
      );
      window.open(r.download_url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setActionError(err instanceof ApiError ? err.serverMessage : 'Could not generate a download link.');
    }
  }

  async function acceptDoc(documentId: string) {
    setActionError(null);
    setBusyDocId(documentId);
    try {
      await apiPost(`/api/admin/compliance/documents/${documentId}/accept`, {});
      await load();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.serverMessage : 'Could not accept the document.');
    } finally {
      setBusyDocId(null);
    }
  }

  async function rejectDoc(reason: string) {
    if (!rejectFor) return;
    setRejectError(null);
    setRejectBusy(true);
    try {
      await apiPost(`/api/admin/compliance/documents/${rejectFor.id}/reject`, { reason });
      setRejectFor(null);
      await load();
    } catch (err) {
      setRejectError(err instanceof ApiError ? err.serverMessage : 'Could not reject the document.');
    } finally {
      setRejectBusy(false);
    }
  }

  async function finaliseClient() {
    if (!id) return;
    setFinaliseError(null);
    setFinaliseBusy(true);
    try {
      await apiPost(`/api/admin/compliance/clients/${id}/finalise`, {});
      setFinaliseOpen(false);
      await load();
    } catch (err) {
      // Surfaces kyc_finalise_not_ready message to the operator.
      setFinaliseError(err instanceof ApiError ? err.serverMessage : 'Could not finalise this client.');
    } finally {
      setFinaliseBusy(false);
    }
  }

  // ── Derived ────────────────────────────────────────────────────────────────

  const client = packet?.client;
  const documents = packet?.documents ?? [];
  const required = packet?.required ?? [];
  const rollup = packet?.rollup ?? 'pending';
  const sanctions = client?.sanctions_status ?? 'pending';

  // Finalise precondition (mirrors the server 409 gate): rollup==='current'
  // AND sanctions_status==='clear'.
  const finaliseReady = rollup === 'current' && sanctions === 'clear';
  const finaliseDisabledReason = !finaliseReady
    ? (rollup !== 'current'
      ? `All required documents must be accepted (current) — rollup is "${getKycStatusLabel(rollup, 'staff')}".`
      : 'Sanctions screening must be cleared before finalising.')
    : !canWrite
      ? 'Your role can view KYC but cannot finalise.'
      : '';

  // Group documents by category. Superseded rows collapse by default.
  const grouped = useMemo(() => {
    const map = new Map<string, DocumentDto[]>();
    for (const d of documents) {
      const key = d.kyc_category ?? '__uncategorised__';
      const arr = map.get(key) ?? [];
      arr.push(d);
      map.set(key, arr);
    }
    return map;
  }, [documents]);

  // Accepted count among required categories, for the progress line.
  const acceptedRequired = useMemo(() => {
    let n = 0;
    for (const cat of required) {
      const docs = grouped.get(cat) ?? [];
      if (docs.some((d) => d.kyc_review_status === 'accepted')) n += 1;
    }
    return n;
  }, [required, grouped]);

  // ── Render ───────────────────────────────────────────────────────────────

  if (loading) {
    return <div className="card" style={{ marginTop: 20, color: 'var(--fg-mute)' }}>Loading client KYC…</div>;
  }
  if (error || !client) {
    return (
      <div className="card" style={{ marginTop: 20, borderColor: 'var(--red)', color: 'var(--red)' }}>
        {error ?? 'Client not found.'}
        <div style={{ marginTop: 12 }}>
          <Link to="/admin/compliance/queue" className="btn sm">← Back to queue</Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <div style={{ marginBottom: 4 }}>
        <Link to="/admin/compliance/queue" style={{ fontSize: 13, color: 'var(--accent)', textDecoration: 'none' }}>
          ← Compliance queue
        </Link>
      </div>
      <h1 className="portal-h1">{client.name}</h1>

      {actionError && (
        <div className="card" style={{ marginTop: 12, borderColor: 'var(--red)', color: 'var(--red)' }}>
          {actionError}
        </div>
      )}

      <div className="kyc-admin-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 320px) 1fr', gap: 20, marginTop: 16 }}>
        {/* ── Left column: identity + gate + consent + progress ───────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card">
            <div style={{ fontSize: 11, color: 'var(--fg-mute)', textTransform: 'uppercase', letterSpacing: 0.06, marginBottom: 10 }}>
              Entity
            </div>
            <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 14px', fontSize: 13 }}>
              <dt style={{ color: 'var(--fg-mute)' }}>Type</dt>
              <dd style={{ margin: 0 }}>{client.entity_type}</dd>
              <dt style={{ color: 'var(--fg-mute)' }}>Country</dt>
              <dd style={{ margin: 0 }}>{client.country}</dd>
              {client.registration_number && (
                <>
                  <dt style={{ color: 'var(--fg-mute)' }}>Reg. no.</dt>
                  <dd style={{ margin: 0, fontFamily: 'var(--mono)' }}>{client.registration_number}</dd>
                </>
              )}
              <dt style={{ color: 'var(--fg-mute)' }}>Sanctions</dt>
              <dd style={{ margin: 0 }}>
                {sanctions === 'flagged' ? <span style={{ color: 'var(--red)' }}>Flagged</span>
                  : sanctions === 'clear' ? <span style={{ color: 'var(--green)' }}>Clear</span>
                    : <span style={{ color: 'var(--fg-mute)' }}>Pending</span>}
              </dd>
            </dl>
            <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
              <span className={`chip ${rollupChipTone(rollup)}`}>{getKycStatusLabel(rollup, 'staff')}</span>
              <span style={{ fontSize: 12.5, color: 'var(--fg-soft)' }}>
                {acceptedRequired} of {required.length} accepted
              </span>
            </div>
          </div>

          {/* Finalise gate */}
          <div className="card">
            <div style={{ fontWeight: 500, fontSize: 14 }}>Finalise KYC</div>
            <p style={{ fontSize: 12.5, color: 'var(--fg-soft)', marginTop: 4, lineHeight: 1.5 }}>
              {finaliseReady
                ? 'All preconditions met — this client can be cleared for briefs.'
                : finaliseDisabledReason}
            </p>
            <button
              type="button"
              className="btn accent"
              style={{ marginTop: 10, width: '100%', justifyContent: 'center' }}
              disabled={!finaliseReady || !canWrite}
              title={!finaliseReady ? finaliseDisabledReason : (!canWrite ? 'Your role can view KYC but cannot finalise.' : undefined)}
              onClick={() => setFinaliseOpen(true)}
            >
              Finalise client KYC
            </button>
          </div>

          {/* Consent summary */}
          <div className="card">
            <div style={{ fontWeight: 500, fontSize: 14 }}>Consent record</div>
            <div style={{ fontSize: 12.5, color: 'var(--fg-soft)', marginTop: 6, lineHeight: 1.6 }}>
              {packet?.consent.latest_at ? (
                <>
                  Latest accepted {formatDate(packet.consent.latest_at)}
                  {packet.consent.latest_version != null && (
                    <> · version {packet.consent.latest_version}</>
                  )}
                </>
              ) : (
                'No consent record on file yet.'
              )}
            </div>
          </div>
        </div>

        {/* ── Right column: per-document panels grouped by category ──────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {required.length === 0 && documents.length === 0 && (
            <div className="card" style={{ color: 'var(--fg-mute)', fontSize: 13 }}>
              Client has not submitted any KYC documents yet.
            </div>
          )}

          {required.map((cat) => {
            const docs = (grouped.get(cat) ?? []).slice().sort((a, b) =>
              (b.created_at ?? '').localeCompare(a.created_at ?? ''),
            );
            const active = docs.filter((d) => d.kyc_review_status !== 'superseded');
            const superseded = docs.filter((d) => d.kyc_review_status === 'superseded');

            return (
              <article key={cat} className="card">
                <div style={{ fontWeight: 500, fontSize: 14, marginBottom: active.length ? 12 : 0 }}>
                  {categoryLabel(cat)}
                </div>

                {active.length === 0 && (
                  <div style={{ fontSize: 12.5, color: 'var(--fg-mute)' }}>Not provided.</div>
                )}

                {active.map((d) => (
                  <DocCard
                    key={d.id}
                    doc={d}
                    canWrite={canWrite}
                    busy={busyDocId === d.id}
                    onDownload={() => downloadDoc(d.id)}
                    onAccept={() => acceptDoc(d.id)}
                    onReject={() => { setRejectError(null); setRejectFor(d); }}
                  />
                ))}

                {superseded.length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <button
                      type="button"
                      className="btn sm ghost"
                      onClick={() => setShowSuperseded((v) => !v)}
                    >
                      {showSuperseded ? 'Hide' : 'Show'} {superseded.length} replaced
                    </button>
                    {showSuperseded && superseded.map((d) => (
                      <DocCard
                        key={d.id}
                        doc={d}
                        canWrite={canWrite}
                        busy={false}
                        muted
                        onDownload={() => downloadDoc(d.id)}
                        onAccept={() => {}}
                        onReject={() => {}}
                      />
                    ))}
                  </div>
                )}
              </article>
            );
          })}

          {/* Uncategorised / non-required docs that still exist on the client */}
          {Array.from(grouped.entries())
            .filter(([key]) => !required.includes(key))
            .map(([key, docs]) => (
              <article key={key} className="card">
                <div style={{ fontWeight: 500, fontSize: 14, marginBottom: 12 }}>
                  {categoryLabel(key === '__uncategorised__' ? null : key)}
                  <span style={{ fontSize: 11.5, color: 'var(--fg-mute)', fontWeight: 400 }}> · not required</span>
                </div>
                {docs
                  .slice()
                  .sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''))
                  .map((d) => (
                    <DocCard
                      key={d.id}
                      doc={d}
                      canWrite={canWrite}
                      busy={busyDocId === d.id}
                      muted={d.kyc_review_status === 'superseded'}
                      onDownload={() => downloadDoc(d.id)}
                      onAccept={() => acceptDoc(d.id)}
                      onReject={() => { setRejectError(null); setRejectFor(d); }}
                    />
                  ))}
              </article>
            ))}
        </div>
      </div>

      {/* Modals */}
      {rejectFor && (
        <RejectModal
          clientName={client.name}
          doc={rejectFor}
          onConfirm={rejectDoc}
          onCancel={() => setRejectFor(null)}
          busy={rejectBusy}
          error={rejectError}
        />
      )}
      {finaliseOpen && (
        <FinaliseModal
          clientName={client.name}
          onConfirm={finaliseClient}
          onCancel={() => setFinaliseOpen(false)}
          busy={finaliseBusy}
          error={finaliseError}
        />
      )}
    </>
  );
}

// ── Per-document card ────────────────────────────────────────────────────────

function DocCard({ doc, canWrite, busy, muted, onDownload, onAccept, onReject }: {
  doc: DocumentDto;
  canWrite: boolean;
  busy: boolean;
  muted?: boolean;
  onDownload: () => void;
  onAccept: () => void;
  onReject: () => void;
}) {
  const state = doc.kyc_review_status ?? null;
  const reviewable = state === 'in_review';

  return (
    <div
      style={{
        border: '1px solid var(--rule)',
        borderRadius: 'var(--r)',
        padding: '12px 14px',
        marginBottom: 10,
        opacity: muted ? 0.6 : 1,
        background: 'var(--bg)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 500, wordBreak: 'break-word' }}>{doc.filename}</div>
          <div style={{ fontSize: 11.5, color: 'var(--fg-mute)', marginTop: 2 }}>
            Uploaded {formatDate(doc.created_at)}
          </div>
        </div>
        <span className={`chip ${docChipTone(state)}`} style={{ flexShrink: 0 }}>
          {docStateLabel(state)}
        </span>
      </div>

      {/* Accepted — reviewed-by / reviewed-at line */}
      {state === 'accepted' && (
        <div style={{ fontSize: 12, color: 'var(--fg-soft)', marginTop: 8 }}>
          Reviewed {formatDate(doc.reviewed_at)}
          {doc.reviewed_by_staff_id ? ` · by ${doc.reviewed_by_staff_id}` : ''}
          {doc.retention_until ? ` · retained until ${formatDate(doc.retention_until)}` : ''}
        </div>
      )}

      {/* Rejected — inline reason so the reviewer can refresh memory */}
      {state === 'rejected' && doc.rejected_reason && (
        <div style={{ marginTop: 8, padding: 10, borderRadius: 'var(--r-sm)', background: 'rgba(184, 133, 0, 0.08)', color: 'var(--amber)', fontSize: 12.5, lineHeight: 1.5 }}>
          {doc.rejected_reason}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        <button type="button" className="btn sm" onClick={onDownload}>Download</button>
        {reviewable && (
          <>
            <button
              type="button"
              className="btn sm accent"
              onClick={onAccept}
              disabled={!canWrite || busy}
              title={!canWrite ? 'Your role can view KYC but cannot accept documents.' : undefined}
            >
              {busy ? 'Working…' : 'Accept'}
            </button>
            <button
              type="button"
              className="btn sm ghost"
              onClick={onReject}
              disabled={!canWrite || busy}
              title={!canWrite ? 'Your role can view KYC but cannot reject documents.' : undefined}
            >
              Reject
            </button>
          </>
        )}
      </div>
    </div>
  );
}
